import { useCallback, useEffect, useMemo, useState } from "react";
import SubscriptionsListView from "../../components/subscriptions/subscriptions-list";
import CurrentPlanView from "../../components/subscriptions/current-plan";
import Menu from "../../pages/menu/menu";
import {
  Box, CircularProgress, Typography, Button,
  Snackbar
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllPlans, userDetails, createPaymentSession,
  updatePaymentStatus, getUserPackage, getUserActivity,
  getUserPayments, cancelSubscription, resetCancelSubscription,
  resetPaymentSession,
  getActivePlans
} from "../../actions";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { DoneOutlineOutlined, Close } from "@material-ui/icons";
import mixpanel from 'mixpanel-browser';
import '../../components/subscriptions/subscriptions-list';
import { asArray } from "../../utils/list";

export default function SubscriptionsView(props) {
  const { isPlan } = props;
  const [paymentStatus, setPaymentStatus] = useState('');
  const [activePackage, setActivePackage] = useState({});
  const [showPlan, setShowPlan] = useState(null);
  const [userActivityDetails, setUserActivityDetails] = useState({});
  const [open, setOpen] = useState(false);
  const [showPlanSuccessAlt, setShowPlanSuccessAlt] = useState(false);
  const [onPaymentLoadSuccess, setOnPaymentLoadSuccess] = useState(false);
  const [emailNotify, setEmailNotify] = useState(false);
  const [showPaymentIframe, setShowPaymentIframe] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');

  const dispatch = useDispatch();
  let { bookingId } = useParams();
  let location = useLocation();
  const history = useHistory();
  const theme = useTheme();

  const userData = useSelector((state) => {
    return state.studentDetails.userDetails;
  });

  const packageListRes = useSelector((state) => {
    return state.getAllPlans;
  });

  const paymentSession = useSelector((state) => {
    return state.paymentSession?.paymentSession;
  });

  const paymentStatusRes = useSelector((state) => {
    return state.paymentStatus?.paymentUpdateStatus;
  });

  const userPackage = useSelector((state) => {
    return state.userPackage?.userPackage;
  });

  const userActivity = useSelector((state) => {
    return state.userActivity?.userActivity;
  });

  const paymentHistory = useSelector((state) => {
    return state.userPayments?.paymentHistory;
  });

  const cancelSubscriptionReq = useSelector((state) => {
    return state.cancelSubscription?.cancelSubscription;
  });

  const activePlans = useSelector((state) => {
    return state.getActivePlans;
  });

  // B3: a failed checkout round-trips through route state carrying the plan the
  // user was buying, so the list can reopen the same checkout.
  const retryPlanId = location.state?.retryPlanId;

  // The retry state is single-use: the moment the list consumes it, strip it
  // from the history entry so refresh/back-forward navigation cannot replay
  // the forced-open checkout.
  const onRetryConsumed = useCallback(() => {
    history.replace({ pathname: "/subscriptions" });
  }, [history]);

  // Surface a gateway decline reason ONLY when the failure payload provides
  // one. NOTE: this route is ALSO Stripe Checkout's cancel_url
  // (StripeSubscriptionService setCancelUrl) — most arrivals here CANCELLED
  // rather than failed a card, and the booking-status FAILED response carries
  // no decline field today (API gap). So the default copy stays neutral and
  // makes no claim about a decline; a real reason renders automatically if
  // the backend ever supplies one.
  const failureDetail = paymentStatusRes?.failureReason
    || paymentStatusRes?.declineMessage
    || paymentStatusRes?.failureMessage;

  const activePlansList = useMemo(() => asArray(activePlans?.plansList), [activePlans?.plansList]);
  const availablePlans = useMemo(() => asArray(packageListRes?.plans), [packageListRes?.plans]);
  const userPackages = useMemo(() => asArray(userPackage), [userPackage]);
  const paymentHistoryList = useMemo(() => asArray(paymentHistory), [paymentHistory]);
  const safeActivePlans = useMemo(() => ({
    ...activePlans,
    plansList: activePlansList
  }), [activePlans, activePlansList]);

  // Get the active plan ID - check for active subscriptions with valid end dates and isselected flag
  const activePlanId = activePlansList.filter(item => {
    const isActive = item.status === 'ACTIVE';
    const hasValidEndDate = new Date(item.subscriptionEndDt) >= new Date();
    const isSelected = item.iselected;
    return isActive && hasValidEndDate && isSelected;
  })[0]?.plan?.id;

  useEffect(() => {
    if (bookingId) {
      const isSuccess = location.pathname.search('success') > -1;
      const status = isSuccess ? 'success' : 'failure';
      setPaymentStatus(status);
      dispatch(updatePaymentStatus({
        isSuccess,
        bookingId
      }));

      if (isSuccess) {
        mixpanel.track('Subscription bought', {
          packageName: activePackage?.packages?.name
        });
      }
    }

    if (!packageListRes.plans) {
      dispatch(getAllPlans());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    const savedUserId = window.localStorage.getItem("userId");

    if (savedUserId && savedUserId !== "undefined") {
      if (!userData) {
        dispatch(userDetails(savedUserId));
      }
      dispatch(getUserPackage(savedUserId));
      dispatch(getUserActivity(savedUserId));
      dispatch(getUserPayments(savedUserId));
      dispatch(getActivePlans(savedUserId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (paymentSession && paymentSession.checkoutURL) {
      window.open(paymentSession.checkoutURL, '_self');
      // setIframeUrl(paymentSession.checkoutURL);
      setOnPaymentLoadSuccess(true);
      // setShowPaymentIframe(true);
      dispatch(resetPaymentSession());

      mixpanel.track('Subscription payment url opened', {
        packageName: activePackage?.packages?.name
      });
    } else if (paymentSession && paymentSession.booking?.id) {
      setTimeout(() => { dispatch(getUserPayments(savedUserId)) }, 2000);
      // setShowPaymentIframe(false);
      history.push('/subscriptions');
      setOpen(true);
      setEmailNotify(true);
      setOnPaymentLoadSuccess(true);

      const savedUserId = window.localStorage.getItem("userId");
      dispatch(getUserPackage(savedUserId));
      dispatch(resetPaymentSession());

      mixpanel.track('Subscription requested', {
        packageName: activePackage?.packages?.name
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history, paymentSession]);

  useEffect(() => {
    if (userPackage) {
      const activePkg = userPackages.length === 1 ? userPackages[0] :
        userPackages.filter(item => item.status === 'ACTIVE')[0];

      setActivePackage(activePkg || {});
    }
  }, [userPackage, userPackages]);

  useEffect(() => {
    if (userActivity) {
      setUserActivityDetails(userActivity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userActivity]);

  useEffect(() => {
    if (cancelSubscriptionReq) {
      setOpen(true);
      dispatch(resetCancelSubscription());
      const savedUserId = window.localStorage.getItem("userId");
      dispatch(getUserPackage(savedUserId));
      dispatch(getUserPayments(savedUserId));
      dispatch(getActivePlans(savedUserId));

      mixpanel.track('Subscription cancelled', {
        packageName: activePackage?.packages?.name
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelSubscriptionReq]);

  const onConfirmation = (data) => {
    dispatch(createPaymentSession(data));
  }

  const subcriptionEndConfirm = (subscriptionId) => {
    const payment = paymentHistoryList.filter(item => item.subscriptionId === subscriptionId);
    if (payment && payment.length) {
      dispatch(cancelSubscription(payment[0].id));
    }
  }

  const closeStatus = () => {
    if (paymentStatusRes && paymentStatus === 'success') {
      setShowPlanSuccessAlt(true);
      history.push("/subscriptions");
      return;
    }
    if (paymentStatus === 'failure') {
      // B3: carry the plan the user was buying back to the list so "Try Again"
      // reopens the SAME checkout instead of resetting to an empty selection.
      history.push({
        pathname: "/subscriptions",
        state: { retryPlanId: paymentStatusRes?.plan?.id }
      });
      return;
    }
    history.push("/subscriptions");
  }

  const handleClose = () => {
    setOpen(false);
    setEmailNotify(false);
  };

  const handlePlanSuccessAltClose = () => {
    setShowPlanSuccessAlt(false);
    setEmailNotify(false);
  };

  const showPlanDetails = (planObj) => {
    setShowPlan(planObj.plan);
  };

  const hidePlanDetailsModal = () => {
    setShowPlan(null);
  };

  const useStyles = makeStyles(() => ({
    examContainer: {
      display: "flex"
    },
    loaderCont: {
      position: 'absolute',
      left: '50%',
      top: '42%'
    },
    statusCont: {
      display: "flex",
      justifyContent: 'center',
      marginTop: 200,
      flex: 1
    },
    statusContWrapper: {
      boxShadow: '1px 5px 5px var(--border-color)',
      height: 310,
      padding: 20,
      width: 500,
      textAlign: 'center',
      border: '1px solid var(--border-color)',
      [theme.breakpoints.down(600)]: {
        width: 'auto'
      }
    },
    icon: {
      background: '#5cb85c',
      padding: 10,
      borderRadius: 40,
      color: '#fff',
      marginBottom: 5
    },
    iconRed: {
      background: 'red',
      padding: 10,
      borderRadius: 40,
      color: '#fff',
      marginBottom: 5
    },
    header: {
      textAlign: 'center',
      marginBottom: 20
    },
    item: {
      lineHeight: '35px',
      textAlign: 'left'
    },
    itemText: {
      float: 'right',
      fontWeight: 'bold'
    },
    btn: {
      marginTop: 20
    }
  }));

  const classes = useStyles();

  return (
    <Box component="div" className='body-cont'>
      <Snackbar open={open} onClose={handleClose} autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>

        <Box className="success-alert">
          {!emailNotify &&
            <>Your subscription has been cancelled successfully...</>
          }
          {emailNotify &&
            <>An email has been sent to your registered mail id with all the details.
              Your payment will be auto-debited.</>
          }
        </Box>
      </Snackbar>
      <Snackbar open={showPlanSuccessAlt} onClose={handlePlanSuccessAltClose} autoHideDuration={4000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box className="success-alert">
          Congratulations! {paymentStatusRes?.plan?.name} is now active. If you want to switch to another plan,
          you can select from the Active Plans below.
        </Box>
      </Snackbar>
      <Menu />
      {!bookingId &&
        !showPaymentIframe &&
        <>
          {packageListRes?.loading && (
            <Box className={classes.loaderCont}><CircularProgress size={24} className="loader" /></Box>
          )}

          {!isPlan && !packageListRes?.loading &&
            <Box className="wd-100">
              <Typography variant="h4" className="plans-title">
                Plans and Billing
              </Typography>
              <Box className="plans-wrapper">
                <SubscriptionsListView
                  activePackage={activePackage}
                  userActivity={userActivityDetails}
                  packageList={availablePlans.filter(plan => plan.id !== activePlanId)}
                  userData={userData}
                  onPaymentLoadSuccess={onPaymentLoadSuccess}
                  onConfirmation={onConfirmation}
                  showPlanDetailsModal={showPlan}
                  hidePlanDetailsModal={hidePlanDetailsModal}
                  isActivePlan={activePlanId}
                  retryPlanId={retryPlanId}
                  onRetryConsumed={onRetryConsumed}
                />
                <CurrentPlanView
                  activePackage={activePackage}
                  paymentHistory={paymentHistoryList}
                  activePlans={safeActivePlans}
                  userActivity={userActivityDetails}
                  subcriptionEndConfirm={subcriptionEndConfirm}
                  showPlanDetails={showPlanDetails}
                />
              </Box>
            </Box>
          }
        </>
      }

      {
        showPaymentIframe &&
        <iframe src={iframeUrl} title="Payment iframe" className="payment-iframe"></iframe>
      }

      {bookingId &&
        <>
          {paymentStatus === 'success' && paymentStatusRes &&
            <Box className={classes.statusCont}>
              <Box className={classes.statusContWrapper}>
                <Box className={classes.header}>
                  <DoneOutlineOutlined className={classes.icon} />
                  <Typography component="div" variant="h5"><b>Payment Successful</b></Typography>
                </Box>
                <Box>
                  <Typography className={classes.item} >Package name: <span className={classes.itemText}>{paymentStatusRes.plan.name}</span></Typography>
                  <Typography className={classes.item}>Amount paid: <span className={classes.itemText}>${paymentStatusRes.price}</span></Typography>
                  <Typography className={classes.item}>Transaction Id: <span className={classes.itemText}>{paymentStatusRes.id}</span></Typography>
                  <Typography className={classes.item}>Email: <span className={classes.itemText}>{paymentStatusRes.user.email}</span></Typography>
                </Box>
                <Button variant="contained" color="primary" onClick={closeStatus} className={classes.btn}>
                  Subscriptions
                </Button>
              </Box>
            </Box>
          }
          {paymentStatus === 'failure' && paymentStatusRes &&
            <Box className={classes.statusCont}>
              <Box className={classes.statusContWrapper}>
                <Box className={classes.header}>
                  <Close className={classes.iconRed} />
                  <Typography component="div" variant="h5"><b>Payment Failed</b></Typography>
                </Box>
                <Box>
                  <Typography className={classes.item} >Package name: <span className={classes.itemText}>{paymentStatusRes?.plan?.name}</span></Typography>
                  {paymentStatusRes?.paymentIntentId &&
                    <Typography className={classes.item}>Transaction Id: <span className={classes.itemText}>{paymentStatusRes?.paymentIntentId}</span></Typography>
                  }
                  <Typography className={classes.item}>Email: <span className={classes.itemText}>{paymentStatusRes?.user?.email}</span></Typography>
                  {failureDetail
                    ? <Typography className={classes.item}>Reason: <span className={classes.itemText}>{failureDetail}</span></Typography>
                    : <Typography className={classes.item}>Your payment didn't complete.</Typography>
                  }
                </Box>
                <Button variant="contained" color="primary" onClick={closeStatus} className={classes.btn}>
                  Try Again
                </Button>
              </Box>
            </Box>
          }
        </>
      }
    </Box>
  );
}
